import React from 'react';
import './WeaponSummary.css';
import {
    buildWeaponDamageRows,
    buildWeaponMetaRows,
    damageLinesColorStyle,
    formatWeaponRange,
    hasWeaponDamages,
} from '../../core/weapon';
import { calcWeaponHitLines } from '../../core/spellUtils';

// Lower case, like the effect sentences in the label column above them.
const TOTAL_LBL = { fr: 'total', en: 'total', de: 'gesamt', es: 'total', pt: 'total' };
const HEAL_TOTAL_LBL = {
    fr: 'soins', en: 'heals', de: 'Heilung', es: 'curas', pt: 'curas',
};
const NO_VALUE = '-';

const splitValueAndRest = (text) => {
    if (!text) {
        return { value: '', rest: '' };
    }
    const match = text.match(/^([+-]?\d+(?:\s*(?:à|to|-)\s*[+-]?\d+)?)(.*)$/);
    if (match) {
        return {
            value: match[1].trim(),
            rest: match[2].trim(),
        };
    }
    return { value: '', rest: text };
};

const rangeText = (range, lang) => (
    range && (range.min != null || range.max != null)
        ? formatWeaponRange(range.min ?? range.max, range.max ?? range.min, lang)
        : NO_VALUE
);

/**
 * One line of the grid, laid out as two grid rows: the small headers (the dice,
 * or a total's crit rates) on their own row, then the icon, both computed values
 * and the label on the next. Giving the headers a row of their own is what keeps
 * each value on the same line as its icon and label instead of pushed below
 * them. Every cell names its column, so a missing header or icon shifts nothing.
 */
const HitRow = ({ heads, icon, values, label, labelled, style }) => (
    <>
        {heads && (
            <>
                <span className="weapon-damage-base weapon-damage-col--normal" data-testid="weapon-damage-base">
                    {heads[0]}
                </span>
                <span className="weapon-damage-base weapon-damage-col--crit" data-testid="weapon-damage-base">
                    {heads[1]}
                </span>
            </>
        )}
        {icon}
        <span className="weapon-damage-value weapon-damage-col--normal" data-testid="weapon-damage-value" style={style}>
            {values[0]}
        </span>
        <span className="weapon-damage-value weapon-damage-col--crit" data-testid="weapon-damage-value" style={style}>
            {values[1]}
        </span>
        {labelled && <span className="weapon-damage-label">{label}</span>}
    </>
);

/**
 * A closing row: the summed normal and critical columns, then what they sum. No
 * icon — a total spans every element; the label says what it is, and without
 * labels the divider does. The crit rates head the totals once, above the first.
 * An empty icon-sized cell stands in for the icon so the row is as tall as a
 * line's, which is what spaces the rates off the values as the dice are above.
 */
const TotalRow = ({ label, range, critRange, noCrit, lang, style, labelled }) => (
    <HitRow
        icon={<span className="weapon-damage-icon" />}
        values={[rangeText(range, lang), noCrit ? NO_VALUE : rangeText(critRange, lang)]}
        label={label[lang] || label.en}
        labelled={labelled}
        style={style}
    />
);

/**
 * The dice a weapon rolls, and — with `stats` — what they come to on that build:
 * a normal and a critical column per line, each with its base above the computed
 * value, the way SpellDetails lays a spell out. A total closes the block — two,
 * when the weapon heals: the damage it deals and the health it gives back. They are not netted. A healing weapon's heal dice can be wider
 * than its damage dice (Baguette Rhon hits 120-282 and heals 52-1471), so one
 * signed total would swing from "heals a lot" to "hits a little" with no way to
 * read either off it.
 *
 * Without `stats` there is nothing to compute, and the lines stay the single
 * sentence per effect they have always been.
 *
 * `showLabels` closes each line with its own words ("dommages Feu"), which the
 * dice-only rendering above already states. It is a prop and not the default
 * because the column costs width the narrow renderings of this block — the
 * hover tooltip, a result card in the two-column grid — do not have; only a
 * full-width overlay opts in.
 */
const WeaponDamageList = ({
    weapon,
    lang = 'fr',
    className = '',
    stats = null,
    lvl = null,
    showLabels = false,
}) => {
    if (!hasWeaponDamages(weapon)) return null;

    const rows = buildWeaponDamageRows(weapon, lang);
    if (!rows.length) return null;
    const computed = stats ? calcWeaponHitLines({ weapon, stats, lvl }) : null;

    if (!computed) {
        return (
            <div className={`weapon-damages ${className}`}>
                {rows.map((row) => {
                    const { value, rest } = splitValueAndRest(row.text);
                    return (
                        <div
                            key={row.key}
                            className={`weapon-damage-line row weapon-damage-${row.elementKey}`}
                        >
                            {row.icon ? (
                                <img src={row.icon} alt="" className="weapon-damage-icon" />
                            ) : (
                                <span className="weapon-damage-bullet" />
                            )}
                            <span className="weapon-damage-text">
                                {value && (
                                    <span className="weapon-damage-value">{value}</span>
                                )}
                                {rest && (
                                    <span className="weapon-damage-label">
                                        {value ? ' ' : ''}
                                        {rest}
                                    </span>
                                )}
                                {!value && !rest && row.text}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }

    const byIndex = new Map(computed.lines.map((line) => [line.index, line]));
    // A total spans every line it sums, so it is only one element's colour when
    // the weapon deals in one element. The two totals split their lines the way
    // `damageRange` and `healRange` do: a shovel that heals Fire is not a fire
    // weapon, so its heal must not tint the damage above it.
    const damageLines = computed.lines.filter((line) => line.kind !== 'heal');
    const healLines = computed.lines.filter((line) => line.kind === 'heal');
    const totalColor = damageLinesColorStyle(damageLines);
    const healColor = damageLinesColorStyle(healLines);
    // A total of one line would only repeat it: it needs two lines to sum.
    const hasTotal = damageLines.length > 1 && (computed.damageRange?.max || 0) > 0;
    const hasHealTotal = healLines.length > 1 && (computed.healRange?.max || 0) > 0;
    const critPct = Math.round(computed.critPct || 0);
    // Only a weapon that *cannot* crit dashes a column — there is nothing to
    // fill it with. 100% crit does not: the normal hit is still worth pricing,
    // since a crit penalty in the fight puts it back on the table.
    const noCrit = !computed.canCrit;

    return (
        <div
            className={`weapon-damages weapon-damage-grid${showLabels ? ' weapon-damage-grid--labelled' : ''} ${className}`}
        >
            {rows.map((row) => {
                const line = byIndex.get(row.index);
                const lineColor = line ? damageLinesColorStyle([line]) : undefined;
                const icon = row.icon ? (
                    <img src={row.icon} alt="" className="weapon-damage-icon" />
                ) : (
                    <span className="weapon-damage-bullet" />
                );
                if (!line) {
                    // Effects with no element to compute against (-1 PA,
                    // pushback in cells): still the sentence they were.
                    return (
                        <React.Fragment key={row.key}>
                            {icon}
                            <span className="weapon-damage-text weapon-damage-text--wide">
                                {row.text}
                            </span>
                        </React.Fragment>
                    );
                }
                return (
                    <HitRow
                        key={row.key}
                        // A weapon that cannot crit has no crit dice and no crit
                        // values, so that column dashes on its own.
                        heads={[rangeText(line.raw, lang), rangeText(line.critRaw, lang)]}
                        icon={icon}
                        values={[rangeText(line.range, lang), rangeText(line.critRange, lang)]}
                        // The effect's own sentence minus the dice that open it —
                        // the same split the dice-only rendering makes, so both say
                        // it in the same words and the game's own wording carries
                        // lifesteal and heals for free.
                        label={splitValueAndRest(row.text).rest}
                        labelled={showLabels}
                        style={lineColor}
                    />
                );
            })}
            {/* One divider for both totals — a weapon that only heals still
                gets it. */}
            {(hasTotal || hasHealTotal) && <span className="weapon-damage-divider" />}
            {/* The crit rates sit where the dice sit on the lines above, because
                what the reader wants next to a total is how often it lands. Once
                for both totals: they share the same roll. 0% included; a weapon
                that cannot crit has no rate to state. */}
            {(hasTotal || hasHealTotal) && !noCrit && (
                <>
                    <span className="weapon-damage-base weapon-damage-col--normal" data-testid="weapon-damage-base">
                        {`${100 - critPct}%`}
                    </span>
                    <span className="weapon-damage-base weapon-damage-col--crit" data-testid="weapon-damage-base">
                        {`${critPct}%`}
                    </span>
                </>
            )}
            {hasTotal && (
                <TotalRow
                    label={TOTAL_LBL}
                    range={computed.damageRange}
                    critRange={computed.critDamageRange}
                    noCrit={noCrit}
                    lang={lang}
                    style={totalColor}
                    labelled={showLabels}
                />
            )}
            {hasHealTotal && (
                <TotalRow
                    label={HEAL_TOTAL_LBL}
                    range={computed.healRange}
                    critRange={computed.critHealRange}
                    noCrit={noCrit}
                    lang={lang}
                    style={healColor}
                    labelled={showLabels}
                />
            )}
        </div>
    );
};

const WeaponMetaList = ({ weapon, lang = 'fr', className = '' }) => {
    if (!weapon) return null;

    const rows = buildWeaponMetaRows(weapon, lang);
    if (!rows.length) return null;

    return (
        <div className={`weapon-meta ${className}`}>
            {rows.map((row) => (
                <div key={row.key} className="weapon-meta-line row">
                    <span className="weapon-meta-text">{row.text}</span>
                </div>
            ))}
        </div>
    );
};

const WeaponSummary = ({ weapon, lang = 'fr', className = '' }) => {
    if (!weapon || !hasWeaponDamages(weapon)) return null;

    const hasMeta = buildWeaponMetaRows(weapon, lang).length > 0;

    return (
        <div className={`weapon-summary column ${className}`}>
            <WeaponDamageList weapon={weapon} lang={lang} className="weapon-summary-damages" />
            {hasMeta && (
                <WeaponMetaList weapon={weapon} lang={lang} className="weapon-summary-meta" />
            )}
        </div>
    );
};

export { WeaponSummary as default, WeaponDamageList, WeaponMetaList };
